/**
 * Pure state for the TopBar call-transfer banner (contract §1, `call.transfer`).
 * One notice per `call_sid`, newest first; terminal notices disappear 60 s
 * after their event, a ringing one that never got a follow-up after 5 min;
 * at most three are shown and at most ten are kept.
 */

export type TransferStatus = 'requested' | 'answered' | 'no_answer' | 'comeback';

export interface CallTransferEvent {
  call_sid: string;
  customer_phone: string;
  agent_number?: string | null;
  status: TransferStatus;
  target_label?: string | null;
  site_cd?: string | null;
  attempt?: number | null;
  dial_status?: string | null;
  duration_s?: number | null;
  at: string;
}

export interface TransferNotice {
  callSid: string;
  customerPhone: string;
  targetLabel: string;
  status: TransferStatus;
  attempt: number;
  /** ISO time of the event (used for the Verläufe jump and ordering). */
  at: string;
  /**
   * When the notice's state began, in local ms: the event's `at` (so a
   * replayed or late event does not restart the countdown), clamped to the
   * arrival time for a server clock ahead of ours. Drives auto-hide.
   */
  updatedAt: number;
}

/** A finished transfer (answered / no answer / back at the agent) stays this long. */
export const TRANSFER_HIDE_AFTER_MS = 60_000;
/** A ringing transfer whose follow-up never arrived (lost event, crashed call) is dropped after this. */
export const TRANSFER_RINGING_EXPIRY_MS = 5 * 60_000;
/**
 * An event this old on arrival is history, not news: the stream replays its
 * buffer after a reconnect, which must not resurrect finished transfers.
 */
export const TRANSFER_EVENT_MAX_AGE_MS = 60_000;
export const MAX_VISIBLE_TRANSFERS = 3;
/** Bound for the tracked notices (only the newest three are ever shown). */
export const MAX_TRACKED_TRANSFERS = 10;

export const TERMINAL_TRANSFER_STATUSES: ReadonlySet<TransferStatus> = new Set<TransferStatus>([
  'answered',
  'no_answer',
  'comeback',
]);

export const TRANSFER_STATUS_LABELS: Record<TransferStatus, string> = {
  requested: 'klingelt …',
  answered: 'angenommen',
  no_answer: 'niemand erreicht',
  comeback: 'zurück beim Agenten',
};

export function isTransferStatus(value: unknown): value is TransferStatus {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(TRANSFER_STATUS_LABELS, value);
}

export function isTerminalTransfer(status: TransferStatus): boolean {
  return TERMINAL_TRANSFER_STATUSES.has(status);
}

export function transferStatusLabel(status: TransferStatus): string {
  return TRANSFER_STATUS_LABELS[status] ?? status;
}

export function formatPhoneDisplay(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
}

/** Epoch ms of an ISO time, or null when missing/unparseable. */
function parseTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Ordering key: an event without a usable time sorts as the oldest. */
function eventTime(iso: string): number {
  return parseTime(iso) ?? 0;
}

function newestFirst(a: TransferNotice, b: TransferNotice): number {
  return b.updatedAt - a.updatedAt;
}

/** When a notice disappears: 60 s after a terminal state, 5 min after "ringing". */
export function transferExpiresAt(notice: TransferNotice): number {
  const lifetime = isTerminalTransfer(notice.status) ? TRANSFER_HIDE_AFTER_MS : TRANSFER_RINGING_EXPIRY_MS;
  return notice.updatedAt + lifetime;
}

/**
 * Replace the notice for this call. Returns the SAME list (no re-render) for
 * an event that changes nothing: older than what is shown, a duplicate
 * delivery (same `at`, same status), or a stale one (replay) for a call that
 * is not shown. A stale update of a shown call still counts — a replayed
 * "angenommen" must end a "klingelt …" — and then expires right away.
 */
export function upsertTransfer(
  list: TransferNotice[],
  event: CallTransferEvent,
  now: number = Date.now(),
): TransferNotice[] {
  const at = parseTime(event.at);
  const existing = list.find((n) => n.callSid === event.call_sid);
  if (!existing && at !== null && now - at > TRANSFER_EVENT_MAX_AGE_MS) return list;
  if (existing) {
    const shownAt = eventTime(existing.at);
    const incomingAt = eventTime(event.at);
    if (shownAt > incomingAt) return list;
    if (shownAt === incomingAt && existing.status === event.status) return list;
  }
  const notice: TransferNotice = {
    callSid: event.call_sid,
    customerPhone: event.customer_phone,
    targetLabel: event.target_label?.trim() || 'Salon',
    status: event.status,
    attempt: event.attempt ?? existing?.attempt ?? 1,
    at: event.at,
    updatedAt: at === null ? now : Math.min(at, now),
  };
  const others = list.filter((n) => n.callSid !== event.call_sid);
  if (now >= transferExpiresAt(notice)) return others;
  const next = [notice, ...others];
  return next.length > MAX_TRACKED_TRANSFERS ? [...next].sort(newestFirst).slice(0, MAX_TRACKED_TRANSFERS) : next;
}

/** Drop notices whose time is up (see transferExpiresAt). */
export function pruneTransfers(list: TransferNotice[], now: number = Date.now()): TransferNotice[] {
  const kept = list.filter((n) => now < transferExpiresAt(n));
  return kept.length === list.length ? list : kept;
}

/** The (at most three) most recently updated notices, newest first. */
export function visibleTransfers(list: TransferNotice[]): TransferNotice[] {
  return [...list].sort(newestFirst).slice(0, MAX_VISIBLE_TRANSFERS);
}

/** Milliseconds until the next notice must be hidden, or null when there is none. */
export function nextTransferExpiry(list: TransferNotice[], now: number = Date.now()): number | null {
  let soonest: number | null = null;
  for (const n of list) {
    const remaining = Math.max(0, transferExpiresAt(n) - now);
    if (soonest === null || remaining < soonest) soonest = remaining;
  }
  return soonest;
}
